/**
 * Run tasks in bounded waves, attempting every one.
 *
 * The action API writes one row per request, so a bulk edit fans out into as
 * many requests; they go in waves because browsers queue past ~6 per host and a
 * single burst of 300 reads as a stalled UI.
 *
 * Crucially, a failure does not stop the remaining waves. Bailing out early
 * leaves part of the set unsent while the caller rolls its optimistic change
 * back in full, so the screen and the server disagree about the remainder — a
 * cascade over 20 actions could land on 11 of them. Every task is attempted and
 * the first failure is re-thrown, so the caller still reports it and re-syncs.
 */
export async function runInWaves<T>(
  items: T[],
  size: number,
  run: (item: T) => Promise<unknown>,
): Promise<void> {
  const failures: unknown[] = [];
  for (let i = 0; i < items.length; i += size) {
    const settled = await Promise.allSettled(items.slice(i, i + size).map(run));
    for (const result of settled) {
      if (result.status === "rejected") failures.push(result.reason);
    }
  }
  if (failures.length > 0) throw failures[0];
}
